import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  LinearProgress,
  Chip,
  Paper,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  DocumentsClient,
  DocumentDto,
  formatFileSize,
} from '../../app/services/api/documentsClient';
import { DocumentType } from '../../enums/DocumentPermission';

const documentsClient = new DocumentsClient();

interface DocumentLinkerProps {
  /** Currently linked document IDs */
  linkedDocumentIds: number[];
  /** Callback when documents are linked/unlinked */
  onChange: (documentIds: number[]) => void;
  /** Franchise ID for filtering documents */
  franchiseId?: number;
  /** Label for the component */
  label?: string;
  /** Whether the component is read-only */
  readOnly?: boolean;
}

/**
 * Component for linking documents to task templates.
 * Displays linked documents and allows adding/removing links.
 */
const DocumentLinker: React.FC<DocumentLinkerProps> = ({
  linkedDocumentIds,
  onChange,
  franchiseId,
  label = 'Linked Documents',
  readOnly = false,
}) => {
  const [linkedDocuments, setLinkedDocuments] = useState<DocumentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DocumentDto[]>([]);
  const [searching, setSearching] = useState(false);

  // Load linked documents
  useEffect(() => {
    const loadLinkedDocuments = async () => {
      if (linkedDocumentIds.length === 0) {
        setLinkedDocuments([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const docs = await Promise.all(
          linkedDocumentIds.map(id => documentsClient.getDocument(id).catch(() => null)),
        );
        setLinkedDocuments(docs.filter((d): d is DocumentDto => d !== null));
      } catch (error) {
        console.error('Error loading linked documents:', error);
      } finally {
        setLoading(false);
      }
    };
    loadLinkedDocuments();
  }, [linkedDocumentIds]);

  // Search documents
  useEffect(() => {
    if (!dialogOpen || !searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const searchDocuments = async () => {
      setSearching(true);
      try {
        const result = await documentsClient.searchDocuments({
          query: searchQuery,
          franchiseId,
          pageSize: 10,
        });
        // Filter out already linked documents
        setSearchResults(result.documents.filter(d => !linkedDocumentIds.includes(d.id)));
      } catch (error) {
        console.error('Error searching documents:', error);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchDocuments, 300);
    return () => clearTimeout(debounce);
  }, [dialogOpen, searchQuery, franchiseId, linkedDocumentIds]);

  const getDocumentIcon = (documentType: DocumentType) => {
    switch (documentType) {
      case DocumentType.PDF:
        return <PictureAsPdfIcon color="error" />;
      case DocumentType.Image:
        return <ImageIcon color="primary" />;
      case DocumentType.Document:
        return <DescriptionIcon color="info" />;
      default:
        return <InsertDriveFileIcon />;
    }
  };

  const handleLinkDocument = (document: DocumentDto) => {
    if (!linkedDocumentIds.includes(document.id)) {
      onChange([...linkedDocumentIds, document.id]);
      setLinkedDocuments(prev => [...prev, document]);
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleUnlinkDocument = (documentId: number) => {
    onChange(linkedDocumentIds.filter(id => id !== documentId));
    setLinkedDocuments(prev => prev.filter(d => d.id !== documentId));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography
          variant="textMD"
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <LinkIcon fontSize="small" />
          {label}
          {linkedDocuments.length > 0 && (
            <Chip
              size="small"
              label={linkedDocuments.length}
              color="primary"
            />
          )}
        </Typography>
        {!readOnly && (
          <Button
            size="md"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Link Document
          </Button>
        )}
      </Box>

      {loading ? (
        <LinearProgress />
      ) : linkedDocuments.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'grey.50' }}>
          <Typography
            variant="textSM"
            color="text.secondary"
          >
            No documents linked
          </Typography>
          {!readOnly && (
            <Typography
              variant="textXS"
              color="text.secondary"
            >
              Link training guides, policies, or other documents for reference
            </Typography>
          )}
        </Paper>
      ) : (
        <List dense>
          {linkedDocuments.map((doc) => (
            <ListItem
              key={doc.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
              }}
            >
              <ListItemIcon>
                {getDocumentIcon(doc.latestVersion.documentType)}
              </ListItemIcon>
              <ListItemText
                primary={doc.name}
                secondary={
                  <Box
                    component="span"
                    sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                  >
                    <Chip
                      size="small"
                      label={doc.latestVersion.documentType}
                      variant="outlined"
                    />
                    <Typography variant="textXS">{formatFileSize(doc.latestVersion.fileSize)}</Typography>
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  size="small"
                  onClick={() => window.open(`/documents/view/${doc.id}`, '_blank')}
                  title="Open document"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
                {!readOnly && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleUnlinkDocument(doc.id)}
                    title="Unlink document"
                  >
                    <LinkOffIcon fontSize="small" />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {/* Link Document Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSearchQuery('');
          setSearchResults([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link Document</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mt: 1, mb: 2 }}
          />

          {searching && <LinearProgress sx={{ mb: 2 }} />}

          {searchResults.length > 0 ? (
            <List>
              {searchResults.map((doc) => (
                <ListItem
                  key={doc.id}
                  button
                  onClick={() => handleLinkDocument(doc)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemIcon>
                    {getDocumentIcon(doc.latestVersion.documentType)}
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.name}
                    secondary={doc.description || formatFileSize(doc.latestVersion.fileSize)}
                  />
                  {doc.tags && doc.tags.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {doc.tags.slice(0, 2).map((tag) => (
                        <Chip key={tag}
                          size="small"
                          label={tag}
                          variant="outlined" />
                      ))}
                    </Box>
                  )}
                </ListItem>
              ))}
            </List>
          ) : searchQuery.trim() && !searching ? (
            <Typography color="text.secondary"
              align="center"
              sx={{ py: 2 }}>
              No documents found matching &quot;{searchQuery}&quot;
            </Typography>
          ) : (
            <Typography color="text.secondary"
              align="center"
              sx={{ py: 2 }}>
              Enter a search term to find documents
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentLinker;
